import { buildWhereFromFilters } from '../sql/buildWhereFromFilters';
import { buildFBDFilter } from '../sql/buildFBDFilter';
import { uniq } from './FindByDesignResolver';
import { DatabaseClientInterface } from '../sql/DatabaseClientInterface';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { ModuleEnum } from '../enums/ModuleEnum';
import { AttributeValueWithCountInterface } from '../interfaces/AttributeValueWithCountInterface';
import { LocalesEnum } from '../enums/LocalesEnum';
import { AttributeUniqValuesInterface } from '../interfaces/AttributeUniqValuesInterface';
import { DesignEnum } from '../enums/DesignEnum';
import { LocalizedAttributeNames } from '../interfaces/AttributesClassInterface';
import { discontinuedMapping } from './ProductsResolver';
import { RegionsEnum } from '../enums/RegionsEnum';
import { prepareValuesMapping } from '../sql/prepareValuesMapping';
import { buildWhereByModule } from '../sql/buildWhereByModule';
import { buildUniqueValuesSql } from '../sql/buildUniqueValuesSql';
import { buildWhereByShoppingCenter } from '../sql/buildWhereByShoppingCenter';

export class FilterValuesResolver {
    private readonly discontinuedAttr: LocalizedAttributeNames;
    constructor(
        private readonly db: DatabaseClientInterface,
        private readonly createMapping: (locale: LocalesEnum, rawName: LocalesEnum) => (arg: any) => AttributeValueWithCountInterface,
        private readonly mapFBD: (row: any) => { template: string[], linkages: string[] },
        private readonly region: RegionsEnum,
    ) {
        this.discontinuedAttr = discontinuedMapping[this.region];
    }

    private async valuesInner(
        id: string,
        filter: AttributesFilterInputInterface,
        module: ModuleEnum,
        labels: string[],
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<{ values: AttributeValueWithCountInterface[] }> {
        const {
            exclude,
            localized,
            attrValue
        } = prepareValuesMapping(id, rawName);

        const [whereModule, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const [whereFilter, paramsFilter] = buildWhereFromFilters(filter, exclude, rawName);
        const [whereShop, paramsShop] = buildWhereByShoppingCenter(rawName, shoppingCenter);
        const query = buildUniqueValuesSql(
            localized,
            attrValue,
            'product',
            `${whereModule}
                   ${whereFilter.length > 0 ? ` AND ${whereFilter}` : ''}
                   ${whereShop.length > 0 ? ` AND ${whereShop}` : ''}
                   ${labels.length ? ` AND label IN (${labels.map(() => '?').join(',')})` : ''}`
        )
        const params = [...paramsModule, ...paramsFilter, ...paramsShop, ...labels];
        const values = await this.db.getMany(query, params, this.createMapping(locale, rawName));
        return { values };
    }

    async values(
        id: string,
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<AttributeUniqValuesInterface> {
        return this.valuesInner(id, filter, module, [], rawName, locale, shoppingCenter);
    }

    async ByDesignValveValues(
        id: string,
        design: DesignEnum,
        valveFilter: AttributesFilterInputInterface,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<AttributeUniqValuesInterface> {
        const module = ModuleEnum.findByDesign;
        const labels = await this.getParams(valveFilter, design, rawName);
        return this.valuesInner(id, filter, module, labels, rawName, locale, shoppingCenter);
    }

    private async getParams(
        valveFilter: AttributesFilterInputInterface,
        design: DesignEnum,
        rawName: LocalesEnum,
    ): Promise<string[]> {
        const [designWhere, designParams] = buildFBDFilter(design, rawName);
        const [filterWhere, filterParams] = buildWhereFromFilters(valveFilter, [], rawName);
        const sql = `SELECT attributes
                     FROM find_by_design
                     WHERE ${designWhere} ${filterWhere.length > 0 ? ` AND ${filterWhere}` : ''}
        `;
        const params = [...designParams, ...filterParams];
        const records = await this.db.getMany(sql, params, this.mapFBD)
        return (records || []).reduce(
            (acc, { template }) => [...acc, ...template],
            [] as string[],
        ).filter(uniq);
    }
}
